import React, { useState, useEffect } from 'react';
import { formatErrorForUI, ErrorTypes } from '../../utils/apiErrors';

/**
 * ErrorAlert - Enhanced error display component with retry functionality
 *
 * Features:
 * - User-friendly error messages
 * - Retry button with countdown
 * - Different visual styles based on error type
 * - Expandable technical details
 * - Auto-dismiss option
 */
function ErrorAlert({
  error,
  onRetry,
  onDismiss,
  showRetry = true,
  showDetails = false,
  autoDismiss = false,
  autoDismissDelay = 10000,
  className = ''
}) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  // Parse the error for display
  const errorInfo = formatErrorForUI(error);

  // Handle auto-dismiss
  useEffect(() => {
    if (autoDismiss && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss();
      }, autoDismissDelay);
      return () => clearTimeout(timer);
    }
  }, [autoDismiss, autoDismissDelay, onDismiss]);

  // Handle retry with countdown
  const handleRetry = async () => {
    if (!onRetry || isRetrying) return;

    // If there's a recommended delay, show countdown
    if (errorInfo.retryDelay > 1000) {
      setCountdown(Math.ceil(errorInfo.retryDelay / 1000));
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      await new Promise(resolve => setTimeout(resolve, errorInfo.retryDelay));
    }

    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  // Get icon based on error type
  const getIcon = () => {
    switch (errorInfo.type) {
      case ErrorTypes.NETWORK:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
          </svg>
        );
      case ErrorTypes.RATE_LIMIT:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case ErrorTypes.AUTH:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      case ErrorTypes.SERVER:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  // Get background color based on error type
  const getBackgroundClass = () => {
    switch (errorInfo.type) {
      case ErrorTypes.RATE_LIMIT:
      case ErrorTypes.QUOTA_EXCEEDED:
      case ErrorTypes.PLATFORM_LIMIT:
        return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
      case ErrorTypes.AUTH:
        return 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800';
      case ErrorTypes.NETWORK:
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      default:
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    }
  };

  // Get text color based on error type
  const getTextClass = () => {
    switch (errorInfo.type) {
      case ErrorTypes.RATE_LIMIT:
      case ErrorTypes.QUOTA_EXCEEDED:
      case ErrorTypes.PLATFORM_LIMIT:
        return 'text-amber-800 dark:text-amber-200';
      case ErrorTypes.AUTH:
        return 'text-purple-800 dark:text-purple-200';
      case ErrorTypes.NETWORK:
        return 'text-blue-800 dark:text-blue-200';
      default:
        return 'text-red-800 dark:text-red-200';
    }
  };

  // Get icon color based on error type
  const getIconClass = () => {
    switch (errorInfo.type) {
      case ErrorTypes.RATE_LIMIT:
      case ErrorTypes.QUOTA_EXCEEDED:
      case ErrorTypes.PLATFORM_LIMIT:
        return 'text-amber-500';
      case ErrorTypes.AUTH:
        return 'text-purple-500';
      case ErrorTypes.NETWORK:
        return 'text-blue-500';
      default:
        return 'text-red-500';
    }
  };

  return (
    <div
      className={`rounded-lg border p-4 ${getBackgroundClass()} ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`shrink-0 ${getIconClass()}`}>
          {getIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h4 className={`font-medium ${getTextClass()}`}>
            {errorInfo.title}
          </h4>

          {/* Message */}
          <p className={`mt-1 text-sm ${getTextClass()} opacity-90`}>
            {errorInfo.message}
          </p>

          {/* Suggestion */}
          {errorInfo.suggestion && (
            <p className={`mt-2 text-sm ${getTextClass()} opacity-75`}>
              {errorInfo.suggestion}
            </p>
          )}

          {/* Technical details (expandable) */}
          {showDetails && errorInfo.details && (
            <div className="mt-3">
              <button
                onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                className={`text-xs ${getTextClass()} opacity-60 hover:opacity-80 flex items-center gap-1`}
              >
                <svg
                  className={`w-3 h-3 transition-transform ${showTechnicalDetails ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Technical details
              </button>
              {showTechnicalDetails && (
                <pre className={`mt-2 p-2 text-xs rounded-sm bg-black/10 dark:bg-white/10 ${getTextClass()} overflow-x-auto`}>
                  {JSON.stringify(errorInfo.details, null, 2)}
                </pre>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="mt-3 flex items-center gap-3">
            {/* Retry button */}
            {showRetry && errorInfo.canRetry && onRetry && (
              <button
                onClick={handleRetry}
                disabled={isRetrying || countdown > 0}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                  ${isRetrying || countdown > 0
                    ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-60'
                    : 'bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-500'
                  }
                  ${getTextClass()}`}
              >
                {isRetrying ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Retrying...
                  </>
                ) : countdown > 0 ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Retry in {countdown}s
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Try Again
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Dismiss button */}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`shrink-0 p-1 rounded-sm hover:bg-black/10 dark:hover:bg-white/10 transition-colors ${getIconClass()}`}
            aria-label="Dismiss error"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default ErrorAlert;
